/**
 * Cookie-Banner – TTDSG § 25 / DSGVO Art. 6 Abs. 1 lit. a konform
 *
 * Regeln:
 * - Kein Dark Pattern: Ablehnen gleichwertig prominent wie Akzeptieren
 * - Kein Pre-Checked für optionale Kategorien
 * - Entscheidung in localStorage – kein neuerlicher Banner nach Wahl
 * - Notwendige Cookies brauchen keine Einwilligung (§ 25 Abs. 2 TTDSG)
 */

const STORAGE_KEY = 'nt_cookie_consent';

export interface CookieConsent {
  // ISO-Timestamp der Einwilligung (Nachweis für DSGVO Art. 7)
  decidedAt: string;
  // Notwendige Cookies – immer true, keine Einwilligung nötig
  necessary: true;
  // Analyse / Tracking – aktuell kein Anbieter aktiv, Platzhalter
  analytics: boolean;
}

/** Gespeicherte Einwilligung laden. null = noch keine Entscheidung. */
export function getConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookieConsent;
  } catch {
    return null;
  }
}

/** Einwilligung speichern. */
function saveConsent(analytics: boolean): CookieConsent {
  const consent: CookieConsent = {
    decidedAt: new Date().toISOString(),
    necessary: true,
    analytics,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  return consent;
}

/** Banner aus dem DOM entfernen. */
function removeBanner(): void {
  const banner = document.getElementById('nt-cookie-banner');
  banner?.remove();
}

/**
 * Cookie-Banner initialisieren.
 * Fügt Banner zum DOM hinzu, wenn noch keine Entscheidung vorliegt.
 * Gibt die vorhandene oder neue Consent zurück (nach Entscheidung).
 */
export function initCookieBanner(): void {
  if (getConsent() !== null) return;

  const banner = document.createElement('div');
  banner.id = 'nt-cookie-banner';
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-modal', 'false');
  banner.setAttribute('aria-label', 'Cookie-Einstellungen');

  banner.innerHTML = `
    <div class="cb-content">
      <p class="cb-title">■ COOKIE-EINSTELLUNGEN</p>
      <p class="cb-text">
        Wir verwenden technisch notwendige Cookies für Login und Sicherheit (Session, CSRF).
        Diese Cookies können nicht deaktiviert werden, da das Spiel ohne sie nicht funktioniert.
        Weitere Informationen findest du in unserer
        <a href="/datenschutz">Datenschutzerklärung</a>.
      </p>

      <div class="cb-category">
        <label class="cb-category-label">
          <input type="checkbox" checked disabled aria-checked="true" />
          <span>Notwendige Cookies</span>
          <span class="cb-badge cb-badge--required">Erforderlich</span>
        </label>
        <p class="cb-category-desc">
          Session-Cookie (nt_session), CSRF-Token (nt_csrf) – technisch unbedingt erforderlich.
        </p>
      </div>

      <div class="cb-category">
        <label class="cb-category-label">
          <input type="checkbox" id="cb-analytics" aria-checked="false" />
          <span>Analyse-Cookies</span>
          <span class="cb-badge">Optional</span>
        </label>
        <p class="cb-category-desc">
          Derzeit kein Analyse-Anbieter aktiv. Platzhalter für zukünftige Integration.
        </p>
      </div>

      <div class="cb-actions">
        <button type="button" id="cb-reject" class="cb-btn cb-btn--secondary">
          Nur notwendige
        </button>
        <button type="button" id="cb-accept-all" class="cb-btn cb-btn--primary">
          Alle akzeptieren
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(banner);

  // Event-Handler – direkt auf Buttons, keine globalen Listener
  const acceptAllBtn = document.getElementById('cb-accept-all');
  const rejectBtn = document.getElementById('cb-reject');
  const analyticsCheckbox = document.getElementById('cb-analytics') as HTMLInputElement | null;

  acceptAllBtn?.addEventListener('click', () => {
    if (analyticsCheckbox) analyticsCheckbox.checked = true;
    saveConsent(true);
    removeBanner();
  });

  rejectBtn?.addEventListener('click', () => {
    saveConsent(false);
    removeBanner();
  });
}
